import { Pipe, PipeTransform } from '@angular/core';

export type GlobalDateFormat =
  | 'monthYear'          // Oct, 2025
  | 'shortDate'          // 10/29/2025
  | 'mediumDate'         // Oct 29, 2025
  | 'longDate'           // October 29, 2025
  | 'fullDate'           // Tuesday, October 29, 2025
  | 'shortTime'          // 10:30 AM
  | 'mediumTime'         // 10:30:45 AM
  | 'shortDateTime'      // 10/29/2025, 10:30 AM
  | 'mediumDateTime'     // Oct 29, 2025, 10:30:45 AM
  | 'relative'           // 2 days ago
  | 'iso'                // 2025-10-29T10:30:45Z
  | 'dashDate';          // 12-28-2025

@Pipe({
  name: 'globalDateFormat',
  standalone: true
})
export class GlobalDateFormatPipe implements PipeTransform {

  transform(value: any, format: GlobalDateFormat = 'shortDate'): string {
    if (!value) return '';

    let date: Date;

    // Handle different input formats
    if (typeof value === 'string') {
      // Handle "2025-07" format from your API
      if (/^\d{4}-\d{2}$/.test(value)) {
        const [year, month] = value.split('-');
        date = new Date(parseInt(year), parseInt(month) - 1, 1);
      } else {
        date = new Date(value);
      }
    } else if (value instanceof Date) {
      date = value;
    } else {
      return '';
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return '';
    }

    return this.formatDate(date, format);
  }

  private formatDate(date: Date, format: GlobalDateFormat): string {
    const options: Intl.DateTimeFormatOptions = {};

    switch (format) {
      case 'monthYear':
        return date.toLocaleDateString('en-US', {
          month: 'short',
          year: 'numeric'
        });

      case 'shortDate':
        return date.toLocaleDateString('en-US', {
          month: 'numeric',
          day: 'numeric',
          year: 'numeric'
        });

      case 'mediumDate':
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });

      case 'longDate':
        return date.toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        });

      case 'fullDate':
        return date.toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        });

      case 'shortTime':
        return date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });

      case 'mediumTime':
        return date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        });

      case 'shortDateTime':
        return `${this.formatDate(date, 'shortDate')}, ${this.formatDate(date, 'shortTime')}`;

      case 'mediumDateTime':
        return `${this.formatDate(date, 'mediumDate')}, ${this.formatDate(date, 'mediumTime')}`;

      case 'relative':
        return this.getRelativeTime(date);

      case 'iso':
        return date.toISOString();

      case 'dashDate':
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${month}-${day}-${year}`;

      default:
        return this.formatDate(date, 'shortDate');
    }
  }

  private getRelativeTime(date: Date): string {
    const now = new Date();
    const diffTime = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffTime / (1000 * 60));

    if (diffDays > 0) {
      return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
    } else if (diffHours > 0) {
      return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
    } else if (diffMinutes > 0) {
      return diffMinutes === 1 ? '1 minute ago' : `${diffMinutes} minutes ago`;
    } else {
      return 'Just now';
    }
  }
}